import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ResponsiveSheet} from '@chekinapp/ui';
import {useOpenModals} from 'context/openModals';
import {usePath} from 'hooks';
import {useDiscardIVModal} from 'hooks/useDiscardIVModal';
import {PATHS} from 'routes/constants';
import DiscardIVChanges from 'components/ModalSheets/DiscardIVChanges';

export type DiscardChangesModalRef = {
  open: (link?: string) => void;
};

const DiscardChangesModal = React.forwardRef<DiscardChangesModalRef>((_, ref) => {
  const {t} = useTranslation();
  const {isOpenDiscardIVModal, closeDiscardIVModal, openDiscardIVModal, resetIVProcess} =
    useDiscardIVModal();
  const {checkIfOpenDiscardModalAndRedirect} = useOpenModals();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();

  const discardFunction = React.useCallback(
    (link?: string) => {
      const verificationViewsToOpenDiscardModal = [
        PATHS.verification.front,
        PATHS.verification.back,
        PATHS.verification.selfie,
        PATHS.verification.uploadingType,
        PATHS.verification.selfieInstructions,
        PATHS.verification.documentSelection,
        'upload',
        'take',
        PATHS.verification.contacts,
      ];
      const isAddPersonalDataView = pathname.includes(PATHS.addPersonalDataForm);
      const [currentVerificationView] = pathname.split('/').slice(-1);
      const isVerificationViewToOpenDiscardModal =
        verificationViewsToOpenDiscardModal.includes(currentVerificationView);

      if (isAddPersonalDataView) {
        checkIfOpenDiscardModalAndRedirect(link as string);
        return;
      }

      if (isVerificationViewToOpenDiscardModal) {
        openDiscardIVModal();
        return;
      }
      navigate(getFullPath(link ?? PATHS.home));
    },
    [
      pathname,
      openDiscardIVModal,
      checkIfOpenDiscardModalAndRedirect,
      getFullPath,
      navigate,
    ],
  );

  React.useImperativeHandle(
    ref,
    () => {
      return {
        open: discardFunction,
      };
    },
    [discardFunction],
  );

  return (
    <ResponsiveSheet
      open={isOpenDiscardIVModal}
      onClose={closeDiscardIVModal}
      title={t('discard_changes')}
      dialogClassName="!max-w-[460px]"
      contentClassName="px-6 pb-6 pt-4 sm:px-8 sm:pb-8 sm:pt-8"
    >
      <DiscardIVChanges onClose={closeDiscardIVModal} resetIVProcess={resetIVProcess} />
    </ResponsiveSheet>
  );
});

export {DiscardChangesModal};
